import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import { ToolType, type ToolPostPayload } from "@repo/app-types/entities/tool";
import type { NewToolProposalData } from "@repo/app-types/entities/toolProposal";
import type { FC } from "react";
import { Dialog } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useReportEditorProposal } from "../copilot/editorProposalsSlice.js";
import { ToolFormActions } from "./components/toolFormActions.js";
import { useToolForm } from "./hooks/useToolForm.js";
import { ToolProposalBanner } from "./toolProposalBanner.js";

/** An Ask Alpharun proposal for a new tool. */
export interface NewToolProposal {
  id: CopilotProposalId;
  data: NewToolProposalData;
}

export interface CreateToolModalDialogProps {
  type: ToolType;
  /** The proposal the tool is created from, which fills the form. */
  proposal?: NewToolProposal;
  onSave: (data: ToolPostPayload) => Promise<void>;
}

export const CreateToolModalDialog: FC<CreateToolModalDialogProps> = (
  props,
) => {
  const { type, proposal, onSave: handleSave } = props;
  const isWide = type === ToolType.CUSTOM || type === ToolType.CODE;

  const {
    isEditMode,
    isFormValid,
    hasChanges,
    isSaving,
    onSave,
    onDiscard,
    renderForm,
  } = useToolForm({
    type,
    initialChanges: proposal && {
      name: proposal.data.name,
      description: proposal.data.description,
      settings: proposal.data.settings,
    },
    onSave: handleSave,
  });
  useReportEditorProposal(proposal?.id, isSaving);

  return (
    <Dialog
      className={twMerge(
        "mt-4 flex flex-col gap-3",
        isWide ? "w-[700px]" : "w-[500px]",
      )}
    >
      {({ close }) => (
        <>
          {proposal ? <ToolProposalBanner data={proposal.data} /> : null}

          {renderForm()}

          <ToolFormActions
            isEditMode={isEditMode}
            hasChanges={hasChanges}
            isFormValid={isFormValid}
            isSaving={isSaving}
            onSave={onSave}
            onDiscard={onDiscard}
            onCancel={close}
          />
        </>
      )}
    </Dialog>
  );
};
